import cx from 'classnames';
import { useFormikContext } from 'formik';
import React, { useCallback, useMemo, useState } from 'react';
import bt from './bootstrap.module.css';
import { mapItemTypeToBaseFormElement } from './defaults/baseFormElements';
import { mapItemTypeToContainerFormElement } from './defaults/containerFormElements';
import FormElementsEdit from './form-dynamic-edit.jsx';
import PlaceHolder from './form-place-holder.jsx';
import { removeDependantFromTriggersMap } from './functions/dependants';
import { SortableElement } from './sortable-element';
import store from './stores/store';

const mapItemTypeToFormElement = {
  ...mapItemTypeToBaseFormElement,
  ...mapItemTypeToContainerFormElement,
};

const getDataById_ = (data, id) => {
  return data.find(x => x && x.id === id);
};

const Preview = props => {
  const {
    parent,
    registers,
    onUpdateElement,
    onUpdateMultipleElements,
    isRegisterCreationLoading,
    onBulkManipulation,
    formElementsEditRef,
    onAddItem,
    onDeleteItem,
    onSwapElements,
    onToggleEditing,
    onCreateRegister,
    editElement = null,
  } = props;

  const { values, setFieldValue } = useFormikContext();

  const [seq, setSeq] = useState(0);

  const getDataById = useCallback(
    id => {
      return values.data.find(x => x && x.id === id);
    },
    [values.data],
  );

  const moveCard = useCallback(
    (dragIndex, hoverIndex) => {
      if (dragIndex === -1) return;

      const dragCard = values.data[dragIndex];
      const targetCard = values.data[hoverIndex];

      let newData = values.data;

      if (dragCard) {
        newData = newData.with(hoverIndex, dragCard);
      }

      if (targetCard) {
        newData = newData.with(dragIndex, targetCard);
      }
      setFieldValue('data', newData);
    },
    [values.data, setFieldValue],
  );

  const restoreCard = useCallback(
    (item, id) => {
      const parent = getDataById_(values.data, item.data.parentId);
      const oldItem = getDataById_(values.data, id);

      if (parent && oldItem) {
        const newIndex = values.data.indexOf(oldItem);
        const newData = values.data.map(x => {
          if (x.id === item.data.parentId) {
            return {
              ...x,
              childItems: x.childItems.map(idX => (idX === id ? null : idX)),
            };
          }

          return x;
        });

        delete oldItem.parentId;
        delete item.setAsChild;
        delete item.parentIndex;

        item.index = newIndex;
        setSeq(seq => (seq > 100000 ? 0 : seq + 1));

        setFieldValue('data', newData);
      }
    },
    [values.data, setFieldValue],
  );

  const swapChildren = useCallback((data, item, child, col) => {
    if (child.col !== undefined && item.id !== child.parentId) {
      return false;
    }
    if (!(child.col !== undefined && child.col !== col && item.childItems[col])) {
      // No child was assigned yet in both source and target.
      return false;
    }
    const oldId = item.childItems[col];
    const oldItem = getDataById_(data, oldId);
    const oldCol = child.col;

    item.childItems[oldCol] = oldId;
    oldItem.col = oldCol;

    item.childItems[col] = child.id;
    child.col = col;
    store.dispatch('updateOrder', data);
    return true;
  }, []);

  const setAsChild = useCallback(
    (item, child, col, isBusy) => {
      if (swapChildren(values.data, item, child, col)) return;

      if (isBusy) return;

      const oldParent = getDataById_(values.data, child.parentId);
      const oldCol = child.col;

      item.childItems[col] = child.id;
      child.col = col;
      child.parentId = item.id;
      child.parentIndex = values.data.indexOf(item);

      if (oldParent) {
        oldParent.childItems[oldCol] = null;
      }

      const list = values.data.filter(x => x && x.parentId === item.id);
      const toRemove = list.filter(x => item.childItems.indexOf(x.id) === -1);
      let newData = values.data;

      if (toRemove.length) {
        newData = values.data.filter(x => toRemove.indexOf(x) === -1);
      }
      if (!getDataById_(values.data, child.id)) {
        newData.push(child);
      }

      setFieldValue('data', newData);
    },
    [values.data, setFieldValue],
  );

  const insertCard = useCallback(
    (item, hoverIndex, id) => {
      if (id) {
        restoreCard(item, id);
      } else {
        setFieldValue('data', values.data.toSpliced(hoverIndex, 0, item));
      }
    },
    [restoreCard, values.data, setFieldValue],
  );

  const insertElement = useCallback(
    element => {
      setFieldValue('data', [...values.data, element]);
    },
    [values.data, setFieldValue],
  );

  const removeChild = useCallback(
    (item, col) => {
      const oldId = item.childItems[col];
      const oldItem = getDataById_(values.data, oldId);
      if (oldItem) {
        const newData = values.data.filter(x => x !== oldItem);
        item.childItems[col] = null;

        setSeq(seq => (seq > 100000 ? 0 : seq + 1));
        setFieldValue('data', newData);
      }
    },
    [values.data, setFieldValue],
  );

  const _onDestroy = useCallback(
    item => {
      let newData = [...values.data];
      const idsToDelete = [item.id];

      const isProvider = !!item.dependantsIds?.length;
      const isDependant = !!item.providerId;

      if (item.childItems) {
        idsToDelete.push(...item.childItems);
      }

      if (isProvider) {
        idsToDelete.push(...item.dependantsIds);
      }

      if (isDependant) {
        const provider = values.data.find(x => x.id === item.providerId);

        if (provider) {
          provider.dependantsIds = provider.dependantsIds.filter(id => id !== item.id);
          Object.assign(
            provider,
            removeDependantFromTriggersMap(provider, item.id, provider.dependantsIds),
          );
          newData = values.data.map(item => (item.id === provider.id ? { ...provider } : item));
        }
      }

      setFieldValue(
        'data',
        newData.filter(x => !idsToDelete.includes(x?.id)),
      );
    },
    [values.data, setFieldValue],
  );

  const getElement = useCallback(
    (item, index, arr) => {
      const element = mapItemTypeToFormElement[item.type];
      if (!element) return null;
      return (
        <SortableElement
          isBuilder
          isDraggable
          key={item.id}
          id={item.id}
          ComposedComponent={element}
          seq={seq}
          isLastElement={index === arr.length - 1}
          index={index}
          moveCard={moveCard}
          onSwapElements={onSwapElements}
          insertCard={insertCard}
          mutable={false}
          parent={parent}
          onToggleEditing={onToggleEditing}
          sortData={item.id}
          data={item}
          onBulkManipulation={onBulkManipulation}
          onUpdateElement={onUpdateElement}
          onUpdateMultipleElements={onUpdateMultipleElements}
          onAddItem={onAddItem}
          insertElement={insertElement}
          registers={registers}
          {...(item.childItems ? { getDataById } : {})}
          setAsChild={setAsChild}
          removeChild={removeChild}
          _onDestroy={_onDestroy}
        />
      );
    },
    [
      onBulkManipulation,
      onUpdateMultipleElements,
      onUpdateElement,
      moveCard,
      insertCard,
      registers,
      onToggleEditing,
      parent,
      onAddItem,
      insertElement,
      getDataById,
      setAsChild,
      removeChild,
      _onDestroy,
    ],
  );

  const items = useMemo(() => {
    return values.data
      .filter(x => !!x && !x.parentId)
      .map((item, index, arr) => getElement(item, index, arr));
  }, [values.data, getElement]);

  return (
    <div
      className={cx(bt['col-md-9'], bt['float-left'], 'react-form-builder-preview', bt['h-100'], {
        // ToDo: change to h-auto
        'is-editing': !!editElement,
      })}
    >
      <div className="edit-form" ref={formElementsEditRef} style={{ zIndex: 3 }}>
        {editElement && (
          <FormElementsEdit
            onToggleEditing={onToggleEditing}
            element={editElement}
            updateElement={onUpdateElement}
            registers={props.registers}
            onDeleteItem={onDeleteItem}
            onAddItem={onAddItem}
            onCreateRegister={onCreateRegister}
            isRegisterCreationLoading={isRegisterCreationLoading}
            onBulkManipulation={onBulkManipulation}
          />
        )}
      </div>
      <div
        className="Sortable bg-white border rounded-lg overflow-hidden"
        style={{ borderColor: 'rgba(227, 227, 227, 1)', padding: 24 }}
      >
        {items.length ? (
          items
        ) : (
          <PlaceHolder
            id="form-place-holder"
            show={items.length === 0}
            index={items.length}
            moveCard={() => {}}
            insertCard={insertCard}
          />
        )}
      </div>
    </div>
  );
};

export default Preview;
